import React, { useState, useRef, useEffect } from 'react';
import operatorService from '../services/operatorService';
import OperatorResponse from '../components/operator/OperatorResponse';
import FrameworkChipBar from '../components/operator/FrameworkChipBar';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth } from '../hooks';
import '../styles/ComplianceOperator.css';
import '../styles/ComplianceOperator_ultra.css';

const ComplianceOperator = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [queries, setQueries] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('ORACLE');
  const [activeTab, setActiveTab] = useState('query');
  const [queryInput, setQueryInput] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState({
    model: 'LLM 2.5 Flash',
    documentCount: 24775,
    lastUpdate: new Date().toISOString(),
    avgLatency: 0.85
  });
  const contentRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Load system status and frameworks
    loadSystemStatus();
    loadFrameworks();
  }, []);

  useEffect(() => {
    // Scroll to bottom when new query added
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [queries]);

  const loadSystemStatus = async () => {
    try {
      const status = await operatorService.getStatus();
      setSystemStatus(status);
    } catch (error) {
      console.error('Failed to load system status:', error);
    }
  };

  const loadFrameworks = async () => {
    try {
      const data = await operatorService.getFrameworks();
      setFrameworks(data.frameworks || []);
    } catch (error) {
      console.error('Failed to load frameworks:', error);
    }
  };

  const handleQuery = async (queryText) => {
    if (!queryText.trim()) return;

    const queryId = Date.now();
    const newQuery = {
      id: queryId,
      text: queryText,
      timestamp: new Date(),
      status: 'processing',
      frameworks: selectedFrameworks.length > 0 ? selectedFrameworks : null
    };

    setQueries(prev => [...prev, newQuery]);
    setIsProcessing(true);
    setQueryInput(''); // Clear input after submitting

    try {
      const startTime = performance.now();
      const frameworksParam = selectedFrameworks.length > 0 ? selectedFrameworks : null;
      const response = await operatorService.query(queryText, frameworksParam);
      const latency = ((performance.now() - startTime) / 1000).toFixed(2);

      setQueries(prev => prev.map(q =>
        q.id === queryId
          ? { ...q, status: 'complete', response, latency }
          : q
      ));
    } catch (error) {
      console.error('OPERATOR ERROR:', error);
      setQueries(prev => prev.map(q =>
        q.id === queryId
          ? { ...q, status: 'error', error: error.message || 'OPERATOR PROCESSING FAILED' }
          : q
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendMessage = () => {
    if (queryInput.trim() && !isProcessing) {
      handleQuery(queryInput);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('Clear all conversation history?')) {
      setQueries([]);
    }
  };

  const operators = [
    {
      name: 'ORACLE',
      status: 'operational',
      statusLabel: 'Operational',
      description: 'Compliance Q&A with RAG citations',
      active: true
    },
    {
      name: 'SENTINEL',
      status: 'active',
      statusLabel: 'Active',
      description: 'Regulatory change detection',
      active: true
    },
    {
      name: 'LEDGER',
      status: 'active',
      statusLabel: 'Active',
      description: 'Compliance report generation',
      active: true
    },
    {
      name: 'AEGIS',
      status: 'planned',
      statusLabel: 'Planned',
      description: 'Ongoing compliance monitor',
      active: false
    },
    {
      name: 'ATLAS',
      status: 'planned',
      statusLabel: 'Planned',
      description: 'Regulatory landscape mapper',
      active: false
    },
    {
      name: 'NEXUS',
      status: 'planned',
      statusLabel: 'Planned',
      description: 'Data integration hub',
      active: false
    }
  ];

  const quickQuerySamples = [
    'What are the MiCA CASP authorization requirements?',
    'DORA ICT risk management obligations for financial entities',
    'GDPR data processing obligations for financial institutions',
    'AML/CFT customer due diligence requirements under AMLD6',
    'MiFID II best execution requirements',
    'NIS2 cybersecurity obligations for fintechs'
  ];

  const handleRefreshConversation = () => {
    if (queries.length > 0 && window.confirm('Start a new conversation? This will clear all messages.')) {
      setQueries([]);
      setQueryInput('');
    } else if (queries.length === 0) {
      setQueryInput('');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        handleRefreshConversation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [queries]);

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <main className="main-content">
          <div className="content-area">
          {/* Hero Header with Delicate Blue Gradient */}
          <div className="operator-hero-ultra">
            <div className="hero-gradient-bg"></div>
            <div className="hero-content-wrapper">
              <div className="hero-breadcrumb">Compliance Terminal › AI-Powered Regulatory Intelligence</div>
              <h1 className="hero-title">AI-Powered Regulatory Intelligence</h1>
              <p className="hero-subtitle">Real-time compliance guidance across MiCA, DORA, GDPR, AML/CFT, and EU financial services regulations. Powered by advanced language models with regulatory expertise.</p>
              <div className="hero-stats-row">
                <div className="hero-stat">
                  <div className="hero-stat-label">Documents Indexed</div>
                  <div className="hero-stat-value">{systemStatus.documentCount?.toLocaleString()}</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-label">Avg Response</div>
                  <div className="hero-stat-value">{systemStatus.avgLatency}s</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-label">Model</div>
                  <div className="hero-stat-value">LLM 2.5 Flash</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-label">Region</div>
                  <div className="hero-stat-value">EU/EEA</div>
                </div>
              </div>
            </div>
          </div>

          {/* System Status & Key Features Section */}
          <div className="system-features-section">
            <div className="system-features-container">
              <div className="section-title-ultra">SYSTEM STATUS</div>
              <div className="system-status-grid">
                <div className="status-info-item">
                  <div className="status-info-label">AI Platform</div>
                  <div className="status-info-value">Cloud-Based RAG</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Generative Model</div>
                  <div className="status-info-value">LLM 2.5 FLASH</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Model Version</div>
                  <div className="status-info-value">LLM-2.5-FLASH-001</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Avg Response Time</div>
                  <div className="status-info-value">{systemStatus.avgLatency}s</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Embedding Model</div>
                  <div className="status-info-value">TEXT-EMBEDDING-004</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Region</div>
                  <div className="status-info-value">EUROPE-WEST1</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Active Modules</div>
                  <div className="status-info-value">6 OPERATORS</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">System Time</div>
                  <div className="status-info-value">{currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} (CET)</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">Data Residency</div>
                  <div className="status-info-value">EU/EEA</div>
                </div>
                <div className="status-info-item">
                  <div className="status-info-label">HSM Location</div>
                  <div className="status-info-value">NORTH-EUROPE</div>
                </div>
              </div>

              <div className="section-title-ultra">KEY FEATURES</div>
              <div className="key-features-grid">
                <div className="key-feature-item">
                  <div className="feature-icon">▸</div>
                  <div className="feature-content">
                    <div className="feature-title">Native Code Execution</div>
                    <div className="feature-description">Can generate and execute Python code</div>
                  </div>
                </div>
                <div className="key-feature-item">
                  <div className="feature-icon">▸</div>
                  <div className="feature-content">
                    <div className="feature-title">Function Calling</div>
                    <div className="feature-description">Supports structured tool use</div>
                  </div>
                </div>
                <div className="key-feature-item">
                  <div className="feature-icon">▸</div>
                  <div className="feature-content">
                    <div className="feature-title">JSON Mode</div>
                    <div className="feature-description">Structured output guaranteed</div>
                  </div>
                </div>
                <div className="key-feature-item">
                  <div className="feature-icon">▸</div>
                  <div className="feature-content">
                    <div className="feature-title">Long Context</div>
                    <div className="feature-description">1M token window for large document sets</div>
                  </div>
                </div>
                <div className="key-feature-item">
                  <div className="feature-icon">▸</div>
                  <div className="feature-content">
                    <div className="feature-title">Multilingual</div>
                    <div className="feature-description">European language support (EN, FR, DE, ES, IT, etc.)</div>
                  </div>
                </div>
                <div className="key-feature-item">
                  <div className="feature-icon">▸</div>
                  <div className="feature-content">
                    <div className="feature-title">Low Latency</div>
                    <div className="feature-description">Optimized for real-time responses (~2-3 seconds)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="operator-main-ultra">
            {/* Chat Section (Left) */}
            <div className="chat-column-ultra">
              <div className="chat-container-glass">
                <div className="chat-header-ultra">
                  <div className="chat-header-left">
                    <span className="chat-operator-name">ORACLE</span>
                    <span className="chat-operator-status">Operational</span>
                  </div>
                  <button className="btn-refresh-ultra" onClick={handleRefreshConversation} title="Start new conversation (Ctrl+K)">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C9.84871 2 11.4844 2.87512 12.5 4.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M12 1.5V4.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>

                <div className="chat-messages-ultra" ref={contentRef}>
                  {queries.length === 0 ? (
                    <div className="chat-empty-ultra">
                      <div className="empty-placeholder">
                        <div className="empty-icon">◈</div>
                        <div className="empty-text">Start a conversation with ORACLE</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {queries.map((query) => (
                        <React.Fragment key={query.id}>
                          <div className="msg-user-ultra">
                            <div className="msg-user-bubble-ultra">{query.text}</div>
                          </div>
                          <OperatorResponse query={query} currentUser={currentUser} />
                        </React.Fragment>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                <div className="chat-input-ultra">
                  <div className="input-wrapper-glass">
                    <textarea
                      className="input-textarea-ultra"
                      placeholder="Ask ORACLE about regulatory compliance..."
                      rows="2"
                      value={queryInput}
                      onChange={(e) => setQueryInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isProcessing}
                    />
                    <FrameworkChipBar
                      frameworks={frameworks}
                      selectedFrameworks={selectedFrameworks}
                      onFrameworkChange={setSelectedFrameworks}
                    />
                    <div className="input-actions-ultra">
                      <div className="input-tools-left">
                        <button
                          className="btn-send-ultra"
                          onClick={handleSendMessage}
                          disabled={isProcessing || !queryInput.trim()}
                        >
                          {isProcessing ? (
                            <span>Processing...</span>
                          ) : (
                            <>
                              <span>Send</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M2 8L14 8M14 8L9 3M14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="input-hint-ultra">
                      Press Enter to send · Shift+Enter for new line · Ctrl+K to start new conversation
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar (Right) */}
            <div className="sidebar-column-ultra">
              <div className="sidebar-card-glass">
                <div className="sidebar-header-ultra">
                  <div className="sidebar-icon-ultra">◈</div>
                  <div>
                    <h3 className="sidebar-title-ultra">ORACLE</h3>
                    <p className="sidebar-subtitle-ultra">Compliance Q&A Engine</p>
                  </div>
                </div>
                <p className="sidebar-description-ultra">
                  Ask any question about EU financial services regulation. ORACLE searches {systemStatus.documentCount?.toLocaleString() || '24,775'} indexed documents and provides cited answers grounded in official regulatory sources.
                </p>

                <div className="sidebar-section-ultra">
                  <h4 className="sidebar-section-title">QUICK QUERIES</h4>
                  <div className="sample-questions-ultra">
                    {quickQuerySamples.map((sample, idx) => (
                      <button
                        key={idx}
                        className="sample-question-btn"
                        onClick={() => handleQuery(sample)}
                      >
                        {sample}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sidebar-section-ultra">
                  <h4 className="sidebar-section-title">Other Operators</h4>
                  <div className="operators-list-ultra">
                    {operators.slice(1).map((op) => (
                      <div
                        key={op.name}
                        className={`operator-item-ultra ${!op.active ? 'disabled' : ''}`}
                        onClick={() => op.active && setSelectedOperator(op.name)}
                      >
                        <div className="operator-item-header">
                          <span className="operator-item-name">{op.name}</span>
                          <span className="operator-item-status">{op.statusLabel}</span>
                        </div>
                        <p className="operator-item-desc">{op.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    </>
  );
};

export default ComplianceOperator;
